import React from 'react';
import {
  QuestionnaireItemOption,
  IQuestionnaireItemOpenChoiceComponent,
} from '../../modules/unsw-fhir/Questionnaire';
import { Conditionals } from '../Fields/Conditional';
import { generateSimpleEnableWhenPredicate } from '../../modules/unsw-fhir/QuestionnaireResponse/helpers';
import { QuestionnaireItemValidator } from '../../modules/unsw-fhir-questionnaire/Utilities/Validators';
import moment from 'moment';
import {
  getPromptTexts,
  getSupportingLinkUris,
  isExclusive,
} from '../../modules/unsw-fhir/Questionnaire/extensions';
import SingleChoiceField from '../Fields/SingleChoiceField';
import MultiChoiceField from '../Fields/MultiChoiceField';
import {
  isQuestionnaireOptionCoding,
  isQuestionnaireOptionTime,
  isQuestionnaireOptionDate,
  isQuestionnaireOptionInteger,
  isQuestionnaireOptionString,
} from '../../modules/unsw-fhir/Questionnaire/typeGuards';
import { OptionItem } from '../../components/Inputs/SingleSelect';
import { useModal } from '../../modules/unsw-modals';

const createOptionList = (
  options: QuestionnaireItemOption[] = [],
): OptionItem[] => {
  const list = options.map<OptionItem>((opt, index) => {
    const exclusive = isExclusive(opt);
    if (isQuestionnaireOptionCoding(opt))
      return {
        id: index.toString(),
        display: opt.valueCoding.display || '',
        code: opt.valueCoding.code || '-1',
        data: opt,
        exclusive,
      };
    if (isQuestionnaireOptionTime(opt))
      return {
        id: index.toString(),
        display: moment(opt.valueTime).format(),
        code: opt.valueTime,
        data: opt,
        exclusive,
      };
    if (isQuestionnaireOptionDate(opt))
      return {
        id: index.toString(),
        display: moment(opt.valueDate).format(),
        code: opt.valueDate,
        data: opt,
        exclusive,
      };
    if (isQuestionnaireOptionInteger(opt))
      return {
        id: index.toString(),
        display: opt.valueInteger.toString(),
        code: opt.valueInteger.toString(),
        data: opt,
        exclusive,
      };
    if (isQuestionnaireOptionString(opt))
      return {
        id: index.toString(),
        display: opt.valueString,
        code: opt.valueString,
        data: opt,
        exclusive,
      };
    throw new Error('Unable to process unknown questionnaire option type');
  });
  return list;
};
const QuestionnaireOpenChoiceItem = (
  props: IQuestionnaireItemOpenChoiceComponent,
) => {
  const { enableWhen, ...rest } = props;
  const prompts = getPromptTexts(props);
  const supportLinks = getSupportingLinkUris(props);
  const Component = props.repeats ? MultiChoiceField : SingleChoiceField;
  const options = createOptionList(props.option);
  const { openSupportingLinks } = useModal();
  return enableWhen ? (
    <Conditionals
      question={props.linkId}
      enableWhens={enableWhen.map(ew => ({
        when: ew.question,
        is: generateSimpleEnableWhenPredicate(ew),
      }))}>
      {({ enabled }) => (
        <Component
          informationText={prompts}
          name={props.linkId}
          supportingLink={
            supportLinks.length !== 0
              ? () => openSupportingLinks(supportLinks)
              : undefined
          }
          validate={QuestionnaireItemValidator(props)}
          enabled={enabled}
          {...rest}
          options={options}
        />
      )}
    </Conditionals>
  ) : (
    <Component
      informationText={prompts}
      name={props.linkId}
      supportingLink={
        supportLinks.length !== 0
          ? () => openSupportingLinks(supportLinks)
          : undefined
      }
      validate={QuestionnaireItemValidator(props)}
      {...rest}
      options={options}
    />
  );
};

export default QuestionnaireOpenChoiceItem;
